import { MatDialogConfig } from "@angular/material/dialog";
import { Study } from "./study.model";
/** A type alias for a function that performs an action with no arguments and no return value */
export type okFunction = () => void;

/**
 * Represents configuration data for a common dialog window.
 * Inherits from MatDialogConfig and adds custom fields for managing the dialog's content and actions.
 */
export class CommonDialogData extends MatDialogConfig {
  title: string = '';
  message: string = '';
  okText: string = '';
  dismissText: string = '';
  okAction!: okFunction;
  dismissAction!: okFunction;
  actionText: string = '';
}
/**
 * Interface for the data passed to a save dialog.
 * Contains information about the study being saved.
 */
export interface SaveDialogData {
  study: Study;
}

/**
 * Data contract for the kit lot filter dialog.
 * Contains the complete list of detected kit lots and the currently selected subset.
 */
export interface KitLotFilterDialogData {
  kitLots: string[];
  selectedKitLots: string[];
  /** When true, the dialog also collects a "Number of Assays" count (RMQC run type only). */
  isRmqcRunType?: boolean;
  /** Previously entered number of assays, if any, to prefill the RMQC-only input. */
  numberOfAssays?: number;
}

/**
 * Result closed from the kit lot filter dialog.
 */
export interface KitLotFilterDialogResult {
  kitLots: string[];
  /** Only present when the dialog was opened for the RMQC run type. */
  numberOfAssays?: number;
}
/**
 * Represents the selection state of a column in a table or data grid.
 * Used to track which columns are selected by the user.
 */
export interface ColumnSelection {
  columnName: string;
  isSelected: boolean;
  columnKey?: string;
  kitLot?: string;
  instrumentId?: string;
  assayName?: string;
  beadRegion?: number | string;
}

export type PrintColumnSelectionMode = 'kitLotInstrumentBead' | 'assayBead' | 'assayInstrumentBead';

/**
 * Data contract for the print column selection dialog.
 * Supplies the full set of selectable columns and their current selection state.
 */
export interface PrintColumnSelectionDialogData {
  columns: ColumnSelection[];
  mode?: PrintColumnSelectionMode;
  /**
   * Whether to offer the unadjusted/adjusted choice. Only set when the study carries
   * both halves; with one there is nothing to choose between.
   */
  offerResultBasis?: boolean;
  /**
   * Whether to ask for the material and batch the sheets are filed against, and whether
   * the report can be printed without them. Absent does not ask at all.
   *
   * 'required' is RMQC, whose sheets are filed against a material the study does not
   * record; the server refuses the report without them. 'optional' is PreBulk, which
   * prints them in the footer when supplied and without them when not.
   */
  paginationInfo?: 'required' | 'optional';
  /**
   * Whether to offer the worklist and report content items as an appendix, and how the
   * checkbox starts. Absent does not offer it.
   *
   * Checked for every workflow but RMQC, whose runsheet already states its materials in
   * its own materials block.
   */
  runDetailsDefault?: boolean;
}

/**
 * What the print column selection dialog closes with: the columns to report on, and
 * which halves of the calculation response to build the report from.
 */
export interface PrintColumnSelectionResult {
  columns: ColumnSelection[];
  includeUnadjusted: boolean;
  includeAdjusted: boolean;
  /**
   * Only present when the dialog asked for them and the operator typed them; they print
   * in the report footer. Absent where they were optional and left blank.
   */
  materialNumber?: string;
  batchNumber?: string;
  /** Whether to append the worklist and report content items. False when not offered. */
  includeRunDetails: boolean;
}

export interface RevisionWarningDialogData {
  selectedRevision: string;
  latestRevision: string;
}

/**
 * One row an uploaded Report Items sheet named but that could not be applied.
 */
export interface ReportItemsImportSkippedRow {
  sheetRow: number;
  reason: string;
}

/**
 * Data contract for the Report Items Excel import confirmation dialog.
 * Previews what the uploaded sheet would do to the table before any of it is applied.
 */
export interface ReportItemsImportDialogData {
  fileName: string;
  sheetName: string;
  /** Rows of the table the sheet would overwrite. */
  updateCount: number;
  /** Rows the sheet would add to the table. */
  addCount: number;
  /** Sheet rows missing any of Kit Lot, Instrument ID or Assay Name. */
  incompleteCount: number;
  skippedRows: ReportItemsImportSkippedRow[];
  /** Sheet headers that matched no column of the table. */
  ignoredHeaders: string[];
}

/**
 * Data contract for the lot expiration warning dialog.
 * Shown when the user imports a calibrator or control lot whose expiration date has already passed.
 */
export interface LotExpirationWarningDialogData {
  lotTypeLabel: string;
  lotNumber: string;
  expirationDate: string;
}
